/**
 * Shop Page - Olympic Fit Wellness Collection
 * OFW branded merchandise and apparel
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShoppingBag, Filter } from 'lucide-react';

export default function ShopPage() {
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All Products' },
    { id: 'apparel', name: 'Apparel' },
    { id: 'accessories', name: 'Accessories' },
    { id: 'equipment', name: 'Equipment' },
    { id: 'nutrition', name: 'Nutrition' }
  ];

  const products = [
    {
      id: 'prod_001',
      name: 'OFW Training Tee',
      category: 'apparel',
      price: 35,
      image: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=600',
      badge: 'Best Seller'
    },
    {
      id: 'prod_002',
      name: 'Performance Hoodie',
      category: 'apparel',
      price: 75,
      image: 'https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=600',
      badge: null
    },
    {
      id: 'prod_003',
      name: 'Training Shorts',
      category: 'apparel',
      price: 45,
      image: 'https://images.unsplash.com/photo-1591195853828-11db59a44f6b?w=600',
      badge: null
    },
    {
      id: 'prod_004',
      name: 'OFW Water Bottle',
      category: 'accessories',
      price: 25,
      image: 'https://images.unsplash.com/photo-1602143407151-7111542de6e8?w=600',
      badge: null
    },
    {
      id: 'prod_005',
      name: 'Gym Duffle Bag',
      category: 'accessories',
      price: 89,
      image: 'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=600',
      badge: 'New'
    },
    {
      id: 'prod_006',
      name: 'Resistance Band Set',
      category: 'equipment',
      price: 29,
      image: 'https://images.unsplash.com/photo-1598289431512-b97b0917affc?w=600',
      badge: null
    },
    {
      id: 'prod_007',
      name: 'Premium Yoga Mat',
      category: 'equipment',
      price: 79,
      image: 'https://images.unsplash.com/photo-1601925260368-ae2f83cf8b7f?w=600',
      badge: null
    },
    {
      id: 'prod_008',
      name: 'Protein Powder',
      category: 'nutrition',
      price: 49,
      image: 'https://images.unsplash.com/photo-1593095948071-474c5cc2989d?w=600',
      badge: 'Member Favorite'
    },
    {
      id: 'prod_009',
      name: 'Performance Tank',
      category: 'apparel',
      price: 32,
      image: 'https://images.unsplash.com/photo-1618354691373-d851c5c3a990?w=600',
      badge: null
    },
    {
      id: 'prod_010',
      name: 'Wireless Earbuds',
      category: 'accessories',
      price: 129,
      image: 'https://images.unsplash.com/photo-1590658268037-6bf12165a8df?w=600',
      badge: null
    },
    {
      id: 'prod_011',
      name: 'Foam Roller',
      category: 'equipment',
      price: 35,
      image: 'https://images.unsplash.com/photo-1600881333168-2ef49b341f30?w=600',
      badge: null
    },
    {
      id: 'prod_012',
      name: 'Pre-Workout Supplement',
      category: 'nutrition',
      price: 39,
      image: 'https://images.unsplash.com/photo-1579722821273-0f6c7d44362f?w=600',
      badge: null
    }
  ];

  const filteredProducts = activeCategory === 'all'
    ? products
    : products.filter(p => p.category === activeCategory);

  return (
    <div className="bg-white" data-testid="shop-page">
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-neutral-900">
        <div className="container mx-auto px-6 lg:px-12 text-center">
          <span className="text-sm font-heading tracking-[0.15em] text-neutral-500 mb-4 block">SHOP</span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-normal text-white mb-6 tracking-[0.04em] uppercase">
            OFW Collection
          </h1>
          <p className="text-base md:text-lg text-neutral-400 max-w-2xl mx-auto tracking-wide">
            Premium apparel, accessories, and equipment designed for peak performance.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="sticky top-20 z-30 bg-white border-b border-neutral-200 py-4">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 hide-scrollbar justify-center">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-6 py-3 text-sm font-heading tracking-[0.06em] whitespace-nowrap transition-all ${
                  activeCategory === cat.id
                    ? 'bg-black text-white'
                    : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                }`}
              >
                {cat.name.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between mb-12">
            <p className="text-neutral-500 text-base">
              Showing <span className="font-medium text-neutral-900">{filteredProducts.length}</span> products
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {filteredProducts.map((product) => (
              <div 
                key={product.id} 
                className="group cursor-pointer"
                data-testid={`product-${product.id}`}
              >
                <div className="aspect-square overflow-hidden mb-4 bg-neutral-100 relative">
                  <img 
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  {product.badge && (
                    <div className="absolute top-4 left-4 bg-black text-white px-3 py-1 text-xs font-heading tracking-wider">
                      {product.badge.toUpperCase()}
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <button className="bg-white text-black px-6 py-3 font-heading text-sm tracking-wider transform translate-y-4 group-hover:translate-y-0 transition-transform">
                      QUICK VIEW
                    </button>
                  </div>
                </div>
                
                <h3 className="text-lg font-heading font-normal text-neutral-900 mb-2 tracking-wide group-hover:text-neutral-600 transition-colors">
                  {product.name}
                </h3>
                
                <p className="text-neutral-900 font-medium">${product.price}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Member Benefits */}
      <section className="py-16 lg:py-24 bg-neutral-50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <span className="text-sm font-heading tracking-[0.15em] text-neutral-500 mb-4 block">MEMBER PERKS</span>
            <h2 className="text-3xl md:text-4xl font-heading font-normal text-neutral-900 mb-6 tracking-[0.04em] uppercase">
              Exclusive Member Discounts
            </h2>
            <p className="text-neutral-600 mb-8 tracking-wide">
              OFW members enjoy exclusive discounts on all merchandise. Elite members receive 20% off, Wellness members receive 15% off, and Essentials members receive 10% off.
            </p>
            <Link
              to="/membership"
              className="inline-flex items-center gap-2 bg-black text-white font-heading px-10 py-4 text-sm tracking-[0.08em] hover:bg-neutral-800 transition-colors"
            >
              BECOME A MEMBER <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Collection */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="relative aspect-[4/3] overflow-hidden group cursor-pointer">
              <img 
                src="https://images.unsplash.com/photo-1556906781-9a412961c28c?w=800"
                alt="New Arrivals"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                <div className="text-center">
                  <h3 className="text-2xl font-heading font-normal text-white mb-4 tracking-[0.06em] uppercase">New Arrivals</h3>
                  <span className="inline-flex items-center gap-2 bg-white text-black font-heading px-6 py-3 text-sm tracking-wider">
                    SHOP NOW <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            </div>
            
            <div className="relative aspect-[4/3] overflow-hidden group cursor-pointer">
              <img 
                src="https://images.unsplash.com/photo-1571902943202-507ec2618e8f?w=800"
                alt="Fitness Essentials"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                <div className="text-center">
                  <h3 className="text-2xl font-heading font-normal text-white mb-4 tracking-[0.06em] uppercase">Fitness Essentials</h3>
                  <span className="inline-flex items-center gap-2 bg-white text-black font-heading px-6 py-3 text-sm tracking-wider">
                    SHOP NOW <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
